import { useEffect, useState } from 'react'
import {
  usePioneerStore,
  TIER_CONFIG,
  getTierForSeat,
  formatSerial,
  PioneerTier,
} from '../store/pioneerStore'
import {
  ScreenWrapper,
  EngravingDivider,
  GoldButton,
  GoldCard,
  ProgressDots,
  EightStar,
  SeatCounter,
} from '../components/shared'

// ─── Props from GP1 App.tsx ───────────────────────────────────────────────────

interface Screen4Props {
  remainingSeats?: number
  seatsLoading?: boolean
  initiateCheckout?: (params: { tier: string; userId?: string | null }) => Promise<void>
  paymentLoading?: boolean
  onTierSelected?: (tier: PioneerTier) => void
  onBack?: () => void
}

// ─── Tier badge ───────────────────────────────────────────────────────────────

function TierBadge({ tier, active }: { tier: PioneerTier; active: boolean }) {
  const config = TIER_CONFIG[tier]

  return (
    <div
      style={{
        padding: '8px 14px',
        background: active ? '#C9A34A18' : 'transparent',
        border: `1px solid ${active ? '#C9A34A88' : '#C9A34A22'}`,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        textAlign: 'center',
        minWidth: 80,
      }}
    >
      <p
        style={{
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.6rem',
          letterSpacing: '0.18em',
          color: active ? '#C9A34A' : '#C9A34A55',
          textTransform: 'uppercase',
          marginBottom: 2,
        }}
      >
        {config.label}
      </p>
      <p
        style={{
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.58rem',
          color: active ? '#C9A34A88' : '#C9A34A33',
        }}
      >
        #{config.seatRange}
      </p>
    </div>
  )
}

// ─── Perk list ────────────────────────────────────────────────────────────────

function PerkList({ perks }: { perks: string[] }) {
  return (
    <ul className="flex flex-col gap-2 mt-1">
      {perks.map((perk, i) => (
        <li key={i} className="flex items-start gap-2">
          <div style={{ marginTop: 4, flexShrink: 0 }}>
            <EightStar size={8} color="#C9A34A" />
          </div>
          <span
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.87rem',
              color: '#F5E6C8CC',
              lineHeight: 1.4,
            }}
          >
            {perk}
          </span>
        </li>
      ))}
    </ul>
  )
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function Screen4PioneerOffer({
  remainingSeats: remainingSeatsFromApp,
  initiateCheckout,
  paymentLoading,
  onTierSelected,
  onBack,
}: Screen4Props) {
  const {
    birthData,
    chartData,
    goBack,
    paymentStatus,
    setPaymentStatus,
    setPioneerSeat,
    userId,
    setScreen,
  } = usePioneerStore()

  const [visible, setVisible] = useState(false)
  const [agreed, setAgreed] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60)
    return () => clearTimeout(t)
  }, [])

  // Use remaining seats from App.tsx prop if available, else fall back to a default
  const remainingSeats = remainingSeatsFromApp ?? 888

  // Determine which tier this user would receive based on current seat count
  const nextSeat = 888 - remainingSeats + 1
  const tier = getTierForSeat(nextSeat)
  const tierConfig = TIER_CONFIG[tier]
  const previewSerial = formatSerial(nextSeat)

  async function handlePayment() {
    if (!agreed) return
    setPaymentStatus('pending')

    try {
      if (initiateCheckout) {
        // GP1 App.tsx wired flow — usePayment hook handles Stripe redirect
        onTierSelected?.(tier)
        await initiateCheckout({ tier, userId })
        // Control returns here only if redirect didn't fire (error or mock)
      } else {
        // Fallback mock for development (no Stripe integration wired)
        await new Promise((r) => setTimeout(r, 2000))
        setPioneerSeat({
          seatNumber: nextSeat,
          tier,
          serialNumber: previewSerial,
          assignedAt: new Date().toISOString(),
          userId: userId ?? 'mock',
        })
        setPaymentStatus('success')
        setScreen(5)
      }
    } catch {
      setPaymentStatus('failed')
    }
  }

  if (!chartData) return null

  const isProcessing = paymentStatus === 'pending' || paymentLoading === true

  return (
    <ScreenWrapper>
      <div
        className="flex flex-col items-center min-h-screen px-6 py-12"
        style={{
          maxWidth: 580,
          margin: '0 auto',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        {/* Progress */}
        <ProgressDots current={4} />

        {/* Header */}
        <div className="text-center mt-8 mb-2">
          <EightStar size={28} className="mx-auto mb-4" />
          <h2
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(1.8rem, 5vw, 2.8rem)',
              fontWeight: 700,
              color: '#F5E6C8',
              lineHeight: 1.15,
            }}
          >
            Your Seat Awaits,
            <br />
            <em style={{ color: '#C9A34A' }}>Pioneer</em>
          </h2>
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.9rem',
              color: '#F5E6C877',
              marginTop: '0.75rem',
              lineHeight: 1.6,
            }}
          >
            Based on current availability, you'll receive
            a <strong style={{ color: '#C9A34A' }}>{tierConfig.label} seat</strong>.
            Seats are assigned in order of payment.
          </p>
        </div>

        <EngravingDivider className="w-full my-5" />

        {/* Seat counter */}
        <SeatCounter remaining={remainingSeats} />

        {/* Tier selector — shows all 3, highlights current */}
        <div className="flex gap-3 mt-5 w-full justify-center">
          {(['STELLIUM', 'LUMINARY', 'CELESTIAL'] as PioneerTier[]).map((t) => (
            <TierBadge key={t} tier={t} active={t === tier} />
          ))}
        </div>

        {/* Offer card */}
        <GoldCard glow className="w-full mt-5 p-6">
          {/* Tier header */}
          <div className="flex items-center justify-between">
            <div>
              <p
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.58rem',
                  letterSpacing: '0.22em',
                  color: '#C9A34A88',
                  textTransform: 'uppercase',
                  marginBottom: 4,
                }}
              >
                {tierConfig.label} Pioneer · {tierConfig.seatRange}
              </p>
              <h3
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '1.4rem',
                  color: '#F5E6C8',
                  fontStyle: 'italic',
                }}
              >
                {tierConfig.tagline}
              </h3>
            </div>
            <div className="text-right">
              <p
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.6rem',
                  color: '#C9A34A66',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                One-time
              </p>
              <p
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '2rem',
                  color: '#C9A34A',
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                ฿{tierConfig.price.toLocaleString()}
              </p>
            </div>
          </div>

          <EngravingDivider className="my-4" />

          {/* Perks */}
          <PerkList perks={tierConfig.perks} />

          <EngravingDivider className="my-4" />

          {/* Serial number preview */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              background: '#09081A',
              border: '1px solid #C9A34A22',
              borderRadius: 2,
            }}
          >
            <div>
              <p
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.55rem',
                  letterSpacing: '0.18em',
                  color: '#C9A34A55',
                  textTransform: 'uppercase',
                  marginBottom: 2,
                }}
              >
                Your Seat Preview
              </p>
              <p
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '1.1rem',
                  color: '#C9A34A',
                  letterSpacing: '0.08em',
                }}
              >
                {previewSerial}
              </p>
            </div>
            <div className="text-right">
              <p
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.55rem',
                  color: '#C9A34A55',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                Your chart
              </p>
              <p
                style={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '0.8rem',
                  color: '#F5E6C8AA',
                }}
              >
                {chartData.sun.glyph ?? ''} {chartData.sun.sign} ·{' '}
                {chartData.moon.glyph ?? ''} {chartData.moon.sign}
                {chartData.rising
                  ? ` · ${chartData.rising.glyph ?? ''} ${chartData.rising.sign}`
                  : ''}
              </p>
            </div>
          </div>
        </GoldCard>

        {/* Agreement */}
        <label
          className="flex items-start gap-3 mt-5 w-full cursor-pointer"
          style={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.82rem',
            color: '#F5E6C877',
            lineHeight: 1.5,
          }}
        >
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            style={{ accentColor: '#C9A34A', width: 15, height: 15, marginTop: 2, flexShrink: 0 }}
          />
          I understand that Pioneer seats are non-refundable and non-transferable.
          This secures my permanent place in INFINI's founding story.
        </label>

        {/* Error state */}
        {paymentStatus === 'failed' && (
          <p
            className="mt-3 text-center"
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.7rem',
              color: '#D4663A',
              letterSpacing: '0.08em',
            }}
          >
            Payment could not be processed. Please try again.
          </p>
        )}

        {/* Payment button */}
        <GoldButton
          size="lg"
          className="w-full mt-5"
          onClick={handlePayment}
          loading={isProcessing}
          disabled={!agreed || isProcessing}
        >
          {isProcessing
            ? 'Preparing your seat…'
            : `Secure My Seat · ฿${tierConfig.price.toLocaleString()}`}
        </GoldButton>

        {/* Trust signals */}
        <div className="flex items-center gap-4 mt-4 justify-center flex-wrap">
          {[
            '🔒 Stripe-secured',
            '฿ THB · 2C2P available',
            'Seat assigned on payment',
          ].map((signal) => (
            <span
              key={signal}
              style={{
                fontFamily: '"DM Mono", monospace',
                fontSize: '0.58rem',
                letterSpacing: '0.1em',
                color: '#C9A34A44',
                textTransform: 'uppercase',
              }}
            >
              {signal}
            </span>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-start w-full mt-6">
          <GoldButton
            variant="ghost"
            size="sm"
            onClick={onBack ?? goBack}
            disabled={isProcessing}
          >
            ← Back to Reading
          </GoldButton>
        </div>
      </div>
    </ScreenWrapper>
  )
}
